import { graphql } from 'react-relay';
import React, { memo, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Flex } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { Browser } from '@atlaskit/media-picker';
import type { UploadRejectionData } from '@atlaskit/media-picker';
import type { UploadEndEventPayload } from '@atlaskit/media-picker/types';

import type { GenerateImageProps } from '@atlassian/ai-image-generation';

import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import {
	COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
	COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
	COVER_PICTURE_REMOVE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { getAttachmentSizeLimitErrorFlag } from '@confluence/fabric-media-support/entry-points/attachmentSizeLimitErrorHelpers.ts';
import { useMediaSSRModeAndAuthConfig } from '@confluence/fabric-media-support/entry-points/ssrMediaHelpers.ts';
import {
	createEditorMediaProvider,
	createRendererMediaProvider,
} from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import type { COVERPICTUREWIDTH } from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';

import {
	getRendererCoverPictureInfo,
	propertyToId,
	isAllowedExternalCoverPictureUrl,
} from '../coverPictureHelperFunctions.ts';
import type {
	CoverPictureId,
	EditorPageCoverPictureComponentProps,
} from '../pageCoverPictureTypes.ts';
import { CoverPictureIdPropertyKey } from '../pageCoverPictureTypes.ts';
import {
	DEFAULT_POSITION_VALUE,
	SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH,
} from '../pageCoverPictureConstants.ts';
import CoverPictureQuery, {
	type CoverPictureQuery$data,
	type CoverPictureQuery$variables,
} from '../__generated__/CoverPictureQuery.graphql.ts';

import type {
	GraphQLCoverPictureWidth,
	EditorPageCoverPictureUpdateCoverPictureWidthMutation as EditorPageCoverPictureUpdateCoverPictureWidthMutation$data,
	EditorPageCoverPictureUpdateCoverPictureWidthMutationVariables as EditorPageCoverPictureUpdateCoverPictureWidthMutation$variables,
} from './__types__/EditorPageCoverPictureUpdateCoverPictureWidthMutation.ts';
// eslint-disable-next-line import/no-cycle
import { MediaCoverPicture } from './MediaCoverPicture.tsx';
import { ImagePicker } from './ImagePicker.tsx';
import { AllowedExternalCoverPicture } from './AllowedExternalCoverPicture.tsx';
import { CoverPictureWrapper } from './EditorPageCoverPictureWrapper.tsx';
const i18n = defineMessages({
	errorFlagTitle: {
		id: 'page-cover-picture.error.flag.title',
		description: 'Title within the error flag that appears if some upload image error occurred.',
		defaultMessage: 'We can’t upload your header image',
	},
	errorFlagDescription: {
		id: 'page-cover-picture.error.flag.description',
		description:
			'Description within the error flag that appears if some upload image error occurred.',
		defaultMessage: 'Refresh and try again.',
	},
	aiGenerationErrorFlagTitle: {
		id: 'page-cover-picture.ai-generation.error.flag.title',
		description: 'Title within the error flag when AI header image generation fails.',
		defaultMessage: "We couldn't generate your header image",
	},
	aiGenerationErrorFlagDescription: {
		id: 'page-cover-picture.ai-generation.error.flag.description',
		description: 'Description within the error flag when AI header image generation fails.',
		defaultMessage: 'Try again or choose a different image.',
	},
	aiGenerationErrorFlagDescriptionWithReason: {
		id: 'page-cover-picture.ai-generation.error.flag.description-with-reason',
		description:
			'Description within the error flag when AI header image generation fails with a specific error reason from the API.',
		defaultMessage: '{errorReason}. Try again or choose a different image.',
	},
});

const EditorPageCoverPictureComponent = memo(
	({
		contentId,
		isPublishingContent,
		contentType,
		userOffline,
		hasCoverPicture,
		contentPropertyKey,
		isSmartSpaceOverview,
		coverPictureState,
		isUnpublishedDraft,
		flags,
		floatingToolbarButtonStyle,
		width,
		dynamicCoverImageHeight,
		isReadOnly,
		spaceKey,
		isFixedWidthImageOption,
		aiHeaderImageStatus,
		aiHeaderImageFileId,
		aiHeaderImageError,
		generateAIHeaderImage,
		setAIHeaderImageStatus,
		isSmartCreate,
	}: EditorPageCoverPictureComponentProps & WithFlagsProps) => {
		const [isMediaBrowserOpen, setIsMediaBrowserOpen] = useState<boolean>(false);
		const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);
		const [isUploadingPicture, setIsUploadingPicture] = useState<boolean>(false);
		const [regenerationCount, setRegenerationCount] = useState<number>(0);
		// mediaConfig does not use onUploadRejection when in read only (RendererMediaProvider), but does need it otherwise (EditorMediaProvider).
		const [mediaConfig, setMediaConfig] = useState<{
			collection: string | undefined;
			authProvider: MediaClientConfig['authProvider'];
			onUploadRejection?: (data: UploadRejectionData) => boolean;
			useSha256ForUploads?: boolean;
		} | null>(null);
		// Using ref since onHandleUploadEnd event uses previous cover picture information and upload will fail
		// since the information is from the past render
		const coverPicture = useRef<{
			draft: CoverPictureId | null;
		}>({ draft: null });
		const [optimisticMediaCoverPictureId, setOptimisticMediaCoverPictureId] = useState<
			string | null
		>(null);
		// need this force update since we are using useRef for the cover picture and therefore won't update the image automatically on change
		const [, forceUpdate] = useState<any>();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const isLivePage = useIsCurrentPageLive();
		// need this state since we want to show reposition option right after we upload a photo
		const [shouldShowReposition, setShouldShowReposition] = useState<boolean>(false);

		// AI Image Acceptance Flow state
		const [isRetryingAIImage, setIsRetryingAIImage] = useState<boolean>(false);
		// Track previous status to detect transition from LOADING_WITH_ACCEPTANCE_FLOW
		const prevAIHeaderImageStatusRef = useRef<string | undefined>(undefined);
		// Track previous status to detect transition INTO ERROR state (for showing error flag)
		const prevStatusForErrorFlagRef = useRef<string | null>(aiHeaderImageStatus ?? null);

		const ssrConfig = useMediaSSRModeAndAuthConfig({ contentId, isExternalShare });
		const isServerPlaceholder = useRenderServerPlaceholder(true);
		const hasSSRConfigInSSRModeOrHydration =
			(isServerPlaceholder || window.__SSR_RENDERED__) && ssrConfig;

		const experienceTracker = useContext(ExperienceTrackerContext);
		const { formatMessage } = useIntl();
		const intl = useIntl();
		const smartSpaceOverviewHeight = isSmartSpaceOverview
			? SMART_SPACE_OVERVIEW_FIXED_HEIGHT_FULL_WIDTH
			: undefined;
		const analyticsSource = isSmartSpaceOverview ? 'smartSpaceOverview' : 'editorPageCoverPicture';

		const { updateContentProperty } = useUpdateContentProperty({
			contentId,
			contentType: contentType || 'page',
			options: {
				refetchQueries: () => [
					{
						query: CoverPictureQuery,
						variables: {
							contentId,
							status: ['draft'],
							draftShareId: getDraftShareIdFromQueryParam(),
						},
					},
				],
			},
		});
		const { data, refetch } = useQueryInterop<CoverPictureQuery$data, CoverPictureQuery$variables>(
			CoverPictureQuery,
			{
				context: { single: true },
				variables: {
					contentId,
					status: ['current', 'draft'],
					draftShareId: getDraftShareIdFromQueryParam(),
				},
			},
		);

		const updateCoverPicture = useCallback(() => {
			const properties = data?.content?.nodes?.[0]?.properties?.nodes || [];
			const draft = properties.find((p) => p?.key === CoverPictureIdPropertyKey.DRAFT);
			const published = properties.find((p) => p?.key === CoverPictureIdPropertyKey.PUBLISHED);
			// Smart Space Overview: when mutating PUBLISHED (no draft), display from published
			const propertyToUse =
				contentPropertyKey === CoverPictureIdPropertyKey.PUBLISHED ? published : draft;
			const nextDraft = propertyToId(propertyToUse);
			if (
				coverPictureState.state.hasCoverPicture &&
				coverPicture.current?.draft?.value?.id &&
				!nextDraft &&
				fg('confluence_cover_pic_optimistic_update_fallback')
			) {
				return;
			}

			coverPicture.current = {
				draft: nextDraft,
			};
		}, [data, contentPropertyKey, coverPictureState.state.hasCoverPicture]);

		if (
			// Should be run sync only in SSR environment
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			process.env.REACT_SSR &&
			isSSRStreaming()
		) {
			updateCoverPicture();
		}

		const coverPictureData = getRendererCoverPictureInfo(data, isLivePage);
		const coverPictureDataId = coverPictureData?.coverPicture?.published?.value?.id;

		// Need this since the isUnsplash prop isn't getting the up to date values
		// Check if the cover picture is from Unsplash or Atlassian hosted CDN (dam-cdn or wac-cdn)
		const coverPictureId = coverPicture.current?.draft?.value?.id;

		// Setting this to null so that createLiveDocHeaderImageAnalyticsEvent is not fired until
		// isAllowedExternalCoverPicture is set to either true/false
		const isAllowedExternalCoverPicture =
			coverPictureId === undefined ? null : isAllowedExternalCoverPictureUrl(coverPictureId);

		const isGeneratingHeaderImage =
			aiHeaderImageStatus === 'LOADING' || aiHeaderImageStatus === 'LOADING_WITH_ACCEPTANCE_FLOW';
		const [mutateCoverPictureWidth] = useMutationInterop<
			EditorPageCoverPictureUpdateCoverPictureWidthMutation$data,
			EditorPageCoverPictureUpdateCoverPictureWidthMutation$variables
		>(
			graphql`
				mutation EditorPageCoverPictureUpdateCoverPictureWidthMutation(
					$contentId: ID!
					$coverPictureWidth: GraphQLCoverPictureWidth!
				) {
					updateCoverPictureWidth(
						input: { contentId: $contentId, coverPictureWidth: $coverPictureWidth }
					) {
						content {
							id
						}
					}
				}
			`,
			{
				onError: (error) => {
					if (isUnauthorizedError(error)) {
						markErrorAsHandled(error);
					}
					getMonitoringClient().submitError(error, {
						attribution: Attribution.MIGRATIONS_TOOLING,
					});
				},
			},
		);

		const onCoverPictureUpdatedSubscriptionData = useCallback(
			async (_: string, response: any) => {
				try {
					await refetch();
					const { coverPictureUpdated } = response || {};
					const value = JSON.parse(coverPictureUpdated?.value);
					const version = coverPictureUpdated?.version;
					coverPicture.current['draft'] = { value, version };
					void coverPictureState.setHasCoverPicture(true);
					setIsUploadingPicture(false);
					void coverPictureState.setIsUploadingPicture(false);
					void coverPictureState.setCoverPictureId(value.id);
					forceUpdate(Math.random());
				} catch (error) {
					getMonitoringClient().submitError(error, {
						attribution: Attribution.PAGE_EXPERIENCES,
					});
					markErrorAsHandled(error);
				}
			},
			[refetch, coverPictureState],
		);

		const onCoverPictureWidthUpdatedSubscription = useCallback(
			(_: string, response: any) => {
				const { coverPictureWidthUpdated } = response || {};

				const value = coverPictureWidthUpdated?.coverPictureWidth;
				void coverPictureState.toggleCoverPictureWidthButton(value);
				forceUpdate(Math.random());
			},
			[coverPictureState],
		);

		const onCoverPictureDeletedSubscription = useCallback(async () => {
			try {
				await refetch();
				coverPicture.current['draft'] = null;
				void coverPictureState.setHasCoverPicture(false);
				void coverPictureState.setCoverPictureId(null);
				void coverPictureState.setIsModalOpen(false);
				void coverPictureState.setIsAddHeaderImageButtonFocused(false);
				forceUpdate(Math.random());
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		}, [refetch, coverPictureState]);

		// Smart Space Overview: subscriptions don't fire, so refetch after mutations
		const refetchAfterMutation = useCallback(async () => {
			try {
				await refetch();
				forceUpdate(Math.random());
			} catch (error) {
				getMonitoringClient().submitError(error, {
					attribution: Attribution.PAGE_EXPERIENCES,
				});
				markErrorAsHandled(error);
			}
		}, [refetch]);

		const subscriptionResourceType = contentType || 'page';

		useSubscriptionEvent({
			subscriberId: 'EditingCoverPictureSubscription',
			contentId,
			delta: 'coverPictureUpdated',
			contentType: subscriptionResourceType,
			onDataReceived: onCoverPictureUpdatedSubscriptionData,
		});

		useSubscriptionEvent({
			subscriberId: 'EditorCoverPictureDeletedSubscription',
			contentId,
			delta: 'coverPictureDeleted',
			contentType: subscriptionResourceType,
			onDataReceived: onCoverPictureDeletedSubscription,
		});

		useSubscriptionEvent({
			subscriberId: 'EditorCoverPictureWidthUpdatedSubscription',
			contentId,
			delta: 'coverPictureWidthUpdated',
			contentType: subscriptionResourceType,
			onDataReceived: onCoverPictureWidthUpdatedSubscription,
		});

		const propertyKeyForMutations = contentPropertyKey ?? CoverPictureIdPropertyKey.DRAFT;

		// Check for existing cover picture
		useEffect(() => {
			if (isSSRStreaming()) {
				updateCoverPicture();
			} else {
				const properties = data?.content?.nodes?.[0]?.properties?.nodes || [];
				const draft = properties.find((p) => p?.key === CoverPictureIdPropertyKey.DRAFT);
				const published = properties.find((p) => p?.key === CoverPictureIdPropertyKey.PUBLISHED);
				// Smart Space Overview: when mutating PUBLISHED (no draft), display from published
				const propertyToUse =
					contentPropertyKey === CoverPictureIdPropertyKey.PUBLISHED ? published : draft;
				const nextDraft = propertyToId(propertyToUse);
				if (
					coverPictureState.state.hasCoverPicture &&
					coverPicture.current?.draft?.value?.id &&
					!nextDraft &&
					fg('confluence_cover_pic_optimistic_update_fallback')
				) {
					forceUpdate(Math.random());
					return;
				}

				coverPicture.current = {
					draft: nextDraft,
				};
			}
			forceUpdate(Math.random());
		}, [
			data,
			contentId,
			contentPropertyKey,
			updateCoverPicture,
			coverPictureState.state.hasCoverPicture,
		]);

		// Setup media config
		useEffect(() => {
			const showAttachmentLimitReachedFlag = (rejectionData: UploadRejectionData) => {
				if (flags && rejectionData) {
					void flags
						.showErrorFlag(getAttachmentSizeLimitErrorFlag(rejectionData, intl))
						.then(() => {
							setIsUploadingPicture(false);
							void coverPictureState.setIsUploadingPicture(false);

							experienceTracker.abort({
								name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
								reason: 'Cover picture media upload too large',
							});

							createAnalyticsEvent({
								type: 'sendScreenEvent',
								data: {
									name: 'pageCoverPictureAttachmentTooLargeFlag',
								},
							}).fire();
						});
				}
				return !!rejectionData;
			};

			// If read only, use renderer media provider, as we only need the auth provider from rendererMediaProvider, and user without edit perms (read only) doesn't have access to editorMediaProvider APIs
			// If not read only, use editor media provider
			const createMediaConfig = async () => {
				// Media provider is not supported in SSR, skip it for now.
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (process.env.REACT_SSR) {
					return;
				}
				if (isReadOnly) {
					const { viewMediaClientConfig } = await createRendererMediaProvider({
						contentId,
						isExternalShare: false,
					});
					if (!viewMediaClientConfig) {
						return null;
					}
					setMediaConfig({
						collection: `contentId-${contentId}`,
						authProvider: viewMediaClientConfig.authProvider,
					});
				} else {
					const { uploadParams, uploadMediaClientConfig } = await createEditorMediaProvider({
						contentId,
						contentType: 'page',
						isExternalShare,
						onUploadRejection: flags ? showAttachmentLimitReachedFlag : undefined,
					});

					if (uploadMediaClientConfig == undefined) {
						return null;
					}
					setMediaConfig({
						collection: uploadParams?.collection,
						authProvider: uploadMediaClientConfig.authProvider,
						onUploadRejection: uploadParams?.onUploadRejection,
						useSha256ForUploads: uploadMediaClientConfig?.useSha256ForUploads,
					});
				}
			};

			void createMediaConfig();
		}, [
			contentId,
			isExternalShare,
			flags,
			intl,
			coverPictureState,
			createAnalyticsEvent,
			experienceTracker,
			isReadOnly,
		]);

		// handling user publishes - we only need to worry about firing the analytic event as backend will take care of the
		// published cover picture content property mutation due to scheduled publishes
		useEffect(() => {
			// fire analytic if we're publishing a page with a cover image
			if (isPublishingContent && !!coverPicture.current?.draft?.value.id) {
				if (isUnpublishedDraft) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'createdWithHeaderImage',
							actionSubject: 'page',
							objectId: contentId,
							source: analyticsSource,
						},
					}).fire();
				} else {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'updatedWithHeaderImage',
							actionSubject: 'page',
							objectId: contentId,
							source: analyticsSource,
						},
					}).fire();
				}
			}
		}, [contentId, isPublishingContent, isUnpublishedDraft, createAnalyticsEvent, analyticsSource]);

		useEffect(() => {
			if (isPublishingContent && hasCoverPicture) {
				void coverPictureState.setShouldShowReposition(false);
			}
		}, [hasCoverPicture, isPublishingContent, coverPictureState]);

		const showErrorFlag = () => {
			void flags.showFlag({
				type: 'error',
				title: formatMessage(i18n.errorFlagTitle),
				description: formatMessage(i18n.errorFlagDescription),
				messageType: 'transactional',
				messageId: 'page-cover-picture-error-flag',
			});
			setIsUploadingPicture(false);
			void coverPictureState.setIsUploadingPicture(false);

			experienceTracker.stopOnError({
				name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
				error: new Error('Error with cover picture media upload'),
			});

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'pageCoverPictureErrorFlag',
				},
			}).fire();
		};

		const showAIGenerationErrorFlag = useCallback(
			(errorReason?: string | null) => {
				// Use the API error reason if available, otherwise fall back to generic message
				const description = errorReason
					? formatMessage(i18n.aiGenerationErrorFlagDescriptionWithReason, { errorReason })
					: formatMessage(i18n.aiGenerationErrorFlagDescription);

				void flags.showFlag({
					type: 'error',
					title: formatMessage(i18n.aiGenerationErrorFlagTitle),
					description,
					messageType: 'transactional',
					messageId: 'page-cover-picture-ai-generation-error-flag',
				});

				// Clear loading states
				setIsUploadingPicture(false);
				void coverPictureState.setIsUploadingPicture(false);
				setIsRetryingAIImage(false);

				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'pageCoverPictureAIGenerationErrorFlag',
						attributes: {
							errorReason: errorReason || 'unknown',
						},
					},
				}).fire();
			},
			[flags, formatMessage, coverPictureState, createAnalyticsEvent],
		);

		const handleUploadStart = () => {
			setIsUploadingPicture(true);
			void coverPictureState.setIsUploadingPicture(true);
			void coverPictureState.setIsChangingPicture(false);
			experienceTracker.start({
				name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
			});
		};

		const handleUploadEnd = (event: UploadEndEventPayload) => {
			const fileType = event.file.type;
			const id = event?.file?.id;
			if (!fileType.includes('image')) {
				showErrorFlag();
			} else if (id) {
				const currentAIFCAutoImage = coverPictureState.state.isCurrentAIFCAutoImage;
				// must JSON stringify propertyValue object since mutation expects a string for this parameter
				const propertyValue = JSON.stringify({
					id,
					position: DEFAULT_POSITION_VALUE,
				});

				const updatePromise = updateContentProperty({
					propertyKey: propertyKeyForMutations,
					propertyValue,
					isDelete: false,
					hasExistingProperty: hasCoverPicture,
				});

				if (isSmartSpaceOverview && updatePromise) {
					void updatePromise.then(() => {
						void refetchAfterMutation();
						setIsUploadingPicture(false);
						void coverPictureState.setIsUploadingPicture(false);
					});
				} else if (updatePromise && fg('confluence_cover_pic_optimistic_update_fallback')) {
					coverPicture.current['draft'] = {
						value: { id, position: DEFAULT_POSITION_VALUE },
						version: (coverPicture.current?.draft?.version || 0) + 1,
					};
					void coverPictureState.setCoverPictureId(id);
					void coverPictureState.setHasCoverPicture(true);
					setOptimisticMediaCoverPictureId(id);
					forceUpdate(Math.random());
					void updatePromise.then(async () => {
						forceUpdate(Math.random());

						try {
							await refetch();
						} catch (error) {
							getMonitoringClient().submitError(error, {
								attribution: Attribution.PAGE_EXPERIENCES,
							});
							markErrorAsHandled(error);
						} finally {
							setIsUploadingPicture(false);
							void coverPictureState.setIsUploadingPicture(false);
						}
					});
				}

				setShouldShowReposition(true);
				void coverPictureState.setShouldShowReposition(true);
				void coverPictureState.setIsCurrentAIFCAutoImage(false);
				experienceTracker.succeed({
					name: COVER_PICTURE_MEDIA_UPLOAD_EXPERIENCE,
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: analyticsSource,
						attributes: {
							imageSource: 'uploaded',
							imageId: id,
							inputMethod: 'header',
							isLivePage,
							isAIGenerationEnabled: isExperimentEnabled(
								'cc_page_experiences_ai_header_image_generation',
							),
							isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
								'cc_page_experiences_aifc_auto_generated_header_img',
							),
							isAIFCAutoGeneratedImage: false,
						},
					},
				}).fire();
				if (currentAIFCAutoImage) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'replaced',
							actionSubject: 'image',
							actionSubjectId: 'autoGeneratedAIFCImageReplaced',
							source: analyticsSource,
							attributes: {
								imageSource: 'uploaded',
								inputMethod: 'header',
								isAIGenerationEnabled: isExperimentEnabled(
									'cc_page_experiences_ai_header_image_generation',
								),
							},
						},
					}).fire();
				}
			} else {
				showErrorFlag();
			}
		};

		const handleUnsplashUpload = useCallback(
			(photoURL) => {
				const currentAIFCAutoImage = coverPictureState.state.isCurrentAIFCAutoImage;
				setIsUploadingPicture(true);
				void coverPictureState.setIsUploadingPicture(true);
				const propertyValue = JSON.stringify({
					id: photoURL,
					position: DEFAULT_POSITION_VALUE,
				});
				const updatePromise = updateContentProperty({
					propertyKey: propertyKeyForMutations,
					propertyValue,
					isDelete: false,
					hasExistingProperty: hasCoverPicture,
				});
				if (isSmartSpaceOverview && updatePromise) {
					void updatePromise.then(() => {
						void refetchAfterMutation();
						setIsUploadingPicture(false);
						void coverPictureState.setIsUploadingPicture(false);
					});
				} else if (updatePromise && fg('confluence_cover_pic_optimistic_update_fallback')) {
					coverPicture.current['draft'] = {
						value: { id: photoURL, position: DEFAULT_POSITION_VALUE },
						version: (coverPicture.current?.draft?.version || 0) + 1,
					};
					void coverPictureState.setCoverPictureId(photoURL);
					void coverPictureState.setHasCoverPicture(true);
					forceUpdate(Math.random());
					void updatePromise.then(async () => {
						forceUpdate(Math.random());

						try {
							await refetch();
						} catch (error) {
							getMonitoringClient().submitError(error, {
								attribution: Attribution.PAGE_EXPERIENCES,
							});
							markErrorAsHandled(error);
						} finally {
							setIsUploadingPicture(false);
							void coverPictureState.setIsUploadingPicture(false);
						}
					});
				}
				setShouldShowReposition(true);
				void coverPictureState.setShouldShowReposition(true);
				void coverPictureState.setIsCurrentAIFCAutoImage(false);
				experienceTracker.succeed({
					name: COVER_PICTURE_UNSPLASH_UPLOAD_EXPERIENCE,
				});
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: analyticsSource,
						attributes: {
							imageSource: 'unsplash',
							imageId: photoURL,
							inputMethod: 'header',
							isLivePage,
							isAIGenerationEnabled: isExperimentEnabled(
								'cc_page_experiences_ai_header_image_generation',
							),
							isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
								'cc_page_experiences_aifc_auto_generated_header_img',
							),
							isAIFCAutoGeneratedImage: false,
						},
					},
				}).fire();
				if (currentAIFCAutoImage) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'replaced',
							actionSubject: 'image',
							actionSubjectId: 'autoGeneratedAIFCImageReplaced',
							source: analyticsSource,
							attributes: {
								imageSource: 'unsplash',
								inputMethod: 'header',
								isAIGenerationEnabled: isExperimentEnabled(
									'cc_page_experiences_ai_header_image_generation',
								),
							},
						},
					}).fire();
				}
			},
			[
				coverPictureState,
				createAnalyticsEvent,
				experienceTracker,
				isLivePage,
				updateContentProperty,
				hasCoverPicture,
				propertyKeyForMutations,
				isSmartSpaceOverview,
				refetchAfterMutation,
				refetch,
				analyticsSource,
			],
		);

		const handleAIGeneratedUpload = useCallback(
			(mediaFileId: string, skipStatusUpdate = false, isAIFCAutoGeneratedImage = false) => {
				const propertyValue = JSON.stringify({
					id: mediaFileId,
					position: DEFAULT_POSITION_VALUE,
				});
				const updatePromise = updateContentProperty({
					propertyKey: propertyKeyForMutations,
					propertyValue,
					isDelete: false,
					hasExistingProperty: hasCoverPicture,
				});
				if (isSmartSpaceOverview && updatePromise) {
					void updatePromise.then(() => {
						void refetchAfterMutation();
					});
				} else if (updatePromise && fg('confluence_cover_pic_optimistic_update_fallback')) {
					coverPicture.current['draft'] = {
						value: { id: mediaFileId, position: DEFAULT_POSITION_VALUE },
						version: (coverPicture.current?.draft?.version || 0) + 1,
					};
					void coverPictureState.setCoverPictureId(mediaFileId);
					void coverPictureState.setHasCoverPicture(true);
					setOptimisticMediaCoverPictureId(mediaFileId);
					forceUpdate(Math.random());
					void updatePromise.then(async () => {
						forceUpdate(Math.random());

						try {
							await refetch();
						} catch (error) {
							getMonitoringClient().submitError(error, {
								attribution: Attribution.PAGE_EXPERIENCES,
							});
							markErrorAsHandled(error);
						}
					});
				}
				// Don't set SUCCESSFULLY_SET if we're showing acceptance flow
				// The status will be set when user accepts the image
				if (!skipStatusUpdate) {
					setAIHeaderImageStatus?.('SUCCESSFULLY_SET');
				}
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'added',
						actionSubject: 'image',
						actionSubjectId: 'headerImageAdded',
						source: analyticsSource,
						attributes: {
							imageSource: 'aiGenerated',
							imageId: mediaFileId,
							inputMethod: 'header',
							isLivePage,
							isAIGenerationEnabled: isExperimentEnabled(
								'cc_page_experiences_ai_header_image_generation',
							),
							isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
								'cc_page_experiences_aifc_auto_generated_header_img',
							),
							isAIFCAutoGeneratedImage,
						},
					},
				}).fire();
			},
			[
				createAnalyticsEvent,
				isLivePage,
				setAIHeaderImageStatus,
				updateContentProperty,
				hasCoverPicture,
				propertyKeyForMutations,
				isSmartSpaceOverview,
				refetchAfterMutation,
				refetch,
				coverPictureState,
				analyticsSource,
			],
		);

		const updateCoverPictureState = useCallback(async () => {
			await coverPictureState.setHasCoverPicture(true);
		}, [coverPictureState]);

		useEffect(() => {
			if (
				!isExperimentEnabled('cc_page_experiences_ai_header_image_generation') &&
				!isExperimentEnabled('cc_page_experiences_aifc_auto_generated_header_img')
			) {
				return;
			}

			// Track status transitions to detect when loading completes
			const prevStatus = prevAIHeaderImageStatusRef.current;
			const wasLoadingWithAcceptance = prevStatus === 'LOADING_WITH_ACCEPTANCE_FLOW';
			prevAIHeaderImageStatusRef.current = aiHeaderImageStatus;

			if (isGeneratingHeaderImage) {
				void coverPictureState.setIsUploadingPicture(true);
			} else {
				void coverPictureState.setIsUploadingPicture(false);
				if (aiHeaderImageFileId && aiHeaderImageStatus !== 'SUCCESSFULLY_SET') {
					// We've just observed `aiHeaderImageFileId` transition from
					// null/undefined to a real media file id, which means the AI
					// generation request has succeeded. Immediately clear the
					// locally-cached previous cover picture id so the old header
					// image stops rendering. Without this, there is a brief flash
					// of the previous image between the loading spinner being
					// removed and the new mediaFileId being committed to the
					// cover picture content property (the property update below
					// is async and only takes effect once the GraphQL
					// subscription fires).
					//
					// Crucially this only runs in the success branch — if
					// generation failed, `aiHeaderImageFileId` stays falsy, this
					// code does not run, and the previous header image remains
					// visible to the user.
					coverPicture.current['draft'] = null;

					// Check if we should show acceptance flow:
					// Either from the stored params (when wrapped function is called) or from status transition
					const lastParams = coverPictureState.state
						.lastAIGenerationParams as GenerateImageProps | null;
					const shouldShowAcceptance = lastParams?.showAcceptanceFlow || wasLoadingWithAcceptance;

					const isAIFCAutoGenerated = !shouldShowAcceptance && !!isSmartCreate;
					void coverPictureState.setIsCurrentAIFCAutoImage(isAIFCAutoGenerated);

					try {
						// If showing acceptance flow, don't set status to SUCCESSFULLY_SET yet
						// User needs to accept first
						// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
						handleAIGeneratedUpload(aiHeaderImageFileId, shouldShowAcceptance, isAIFCAutoGenerated);
					} finally {
						void updateCoverPictureState();
					}

					// Show the acceptance flow with repositioning when image is ready
					if (shouldShowAcceptance) {
						// Clear uploading state to allow acceptance UI to show
						// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
						setIsUploadingPicture(false);
						void coverPictureState.setShouldShowAIImageAcceptanceFlow(true);
						// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
						setIsRetryingAIImage(false);
					}
				}
			}
		}, [
			isGeneratingHeaderImage,
			coverPictureState,
			aiHeaderImageFileId,
			handleAIGeneratedUpload,
			updateCoverPictureState,
			aiHeaderImageStatus,
			isSmartCreate,
		]);

		// Watch for AI generation error status and show error flag to user
		// Only show flag on TRANSITION into ERROR state, not on re-renders while in ERROR state
		useEffect(() => {
			const prevStatus = prevStatusForErrorFlagRef.current;
			const currentStatus = aiHeaderImageStatus;

			// Only show error flag when TRANSITIONING into ERROR state
			// This prevents duplicate flags from re-renders while already in ERROR state
			const isTransitioningToError = prevStatus !== 'ERROR' && currentStatus === 'ERROR';

			// Don't show error flags for AIFC auto-generated images (where the image is
			// generated automatically from the title without user interaction).
			// The auto flow uses 'LOADING' status (no acceptance flow), while user-initiated
			// generation uses 'LOADING_WITH_ACCEPTANCE_FLOW'. We detect auto flow by checking
			// if the previous status was 'LOADING' (not 'LOADING_WITH_ACCEPTANCE_FLOW').
			const wasAIFCAutoGenerated = prevStatus === 'LOADING';

			if (isTransitioningToError && !wasAIFCAutoGenerated) {
				// Due to a false positive
				// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
				showAIGenerationErrorFlag(aiHeaderImageError);
			}

			// Update the ref for next render
			prevStatusForErrorFlagRef.current = currentStatus ?? null;
		}, [aiHeaderImageStatus, aiHeaderImageError, showAIGenerationErrorFlag]);

		// Handler for accepting AI generated image (from AIImageAcceptanceRepositioner)
		const onAcceptAIGeneratedImage = useCallback(() => {
			void coverPictureState.setShouldShowAIImageAcceptanceFlow(false);
			setAIHeaderImageStatus?.('SUCCESSFULLY_SET');
			void coverPictureState.setLastAIGenerationParams(null);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'accepted',
					actionSubject: 'aiGeneratedImage',
					actionSubjectId: 'aiGeneratedHeaderImage',
					source: analyticsSource,
					attributes: {
						regenerationCount,
					},
				},
			}).fire();
		}, [
			setAIHeaderImageStatus,
			regenerationCount,
			createAnalyticsEvent,
			coverPictureState,
			analyticsSource,
		]);

		// Handler for retrying AI image generation (from AIImageAcceptanceRepositioner)
		const onRetryAIGeneratedImage = useCallback(async () => {
			const lastParams = coverPictureState.state
				.lastAIGenerationParams as GenerateImageProps | null;

			if (!generateAIHeaderImage) {
				// eslint-disable-next-line no-console
				console.warn('[AI Header Image] generateAIHeaderImage is not available');
				return;
			}

			if (!lastParams) {
				// This should never happen - if the user clicked retry, there should be stored params
				// eslint-disable-next-line no-console
				console.error('[AI Header Image] Cannot retry: no stored generation parameters found');
				return;
			}

			setIsRetryingAIImage(true);
			setRegenerationCount((prev) => prev + 1);
			await generateAIHeaderImage({
				...lastParams,
				showAcceptanceFlow: true,
			});
		}, [generateAIHeaderImage, coverPictureState.state.lastAIGenerationParams]);

		// Wrapped generateAIHeaderImage that stores params for retry functionality
		const wrappedGenerateAIHeaderImage = useCallback(
			async (props: GenerateImageProps) => {
				if (generateAIHeaderImage) {
					// Store params in container state so we can retry with the same params
					// Using container state ensures params survive component re-mounts
					void coverPictureState.setLastAIGenerationParams(props);
					return generateAIHeaderImage(props);
				}
				return undefined;
			},
			[generateAIHeaderImage, coverPictureState],
		);

		const onRemoveImage = useCallback(async () => {
			void coverPictureState.setIsRemovingPicture(true);
			void coverPictureState.setIsAddHeaderImageButtonFocused(false);
			const updatePromise = updateContentProperty({
				propertyKey: propertyKeyForMutations,
				isDelete: true,
				hasExistingProperty: hasCoverPicture,
			});
			if (isSmartSpaceOverview && updatePromise) {
				void updatePromise.then(async () => {
					if (typeof refetch === 'function') {
						await refetch();
					}
					coverPicture.current['draft'] = null;
					setOptimisticMediaCoverPictureId(null);
					void coverPictureState.setHasCoverPicture(false);
					void coverPictureState.setCoverPictureId(null);
					void coverPictureState.setIsModalOpen(false);
					forceUpdate(Math.random());
				});
			} else if (updatePromise && fg('confluence_cover_pic_optimistic_update_fallback')) {
				void updatePromise.then(async () => {
					try {
						if (typeof refetch === 'function') {
							await refetch();
						}
						coverPicture.current['draft'] = null;
						setOptimisticMediaCoverPictureId(null);
						void coverPictureState.setHasCoverPicture(false);
						void coverPictureState.setCoverPictureId(null);
						void coverPictureState.setIsModalOpen(false);
						void coverPictureState.setIsAddHeaderImageButtonFocused(false);
						forceUpdate(Math.random());
					} catch (error) {
						getMonitoringClient().submitError(error, {
							attribution: Attribution.PAGE_EXPERIENCES,
						});
						markErrorAsHandled(error);
					}
				});
			}
			void coverPictureState.setIsChangingPicture(false);
			void coverPictureState.setIsModalOpen(false);
			// Clear AI image acceptance flow state when image is removed/discarded
			if (coverPictureState.state.shouldShowAIImageAcceptanceFlow) {
				void coverPictureState.setShouldShowAIImageAcceptanceFlow(false);
				void coverPictureState.setLastAIGenerationParams(null);
				// Set to SUCCESSFULLY_SET
				setAIHeaderImageStatus?.('SUCCESSFULLY_SET');
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'discarded',
						actionSubject: 'aiGeneratedImage',
						actionSubjectId: 'aiGeneratedHeaderImage',
						source: analyticsSource,
						attributes: {
							regenerationCount,
						},
					},
				}).fire();
			}
			void coverPictureState.setIsCurrentAIFCAutoImage(false);
			void coverPictureState.setIsRemovingPicture(false);
			experienceTracker.succeed({
				name: COVER_PICTURE_REMOVE_EXPERIENCE,
			});
		}, [
			coverPictureState,
			experienceTracker,
			createAnalyticsEvent,
			regenerationCount,
			updateContentProperty,
			hasCoverPicture,
			setAIHeaderImageStatus,
			propertyKeyForMutations,
			isSmartSpaceOverview,
			refetch,
			analyticsSource,
		]);

		const onImageWidthChange = useCallback(
			async (coverPictureWidth: COVERPICTUREWIDTH) => {
				if (coverPictureWidth !== coverPictureState.state.coverPictureWidth) {
					void coverPictureState.toggleCoverPictureWidthButton(coverPictureWidth);

					//hack the input and outout have different capitalizations TT_TT
					// the mutation returns lowercase, the mutation expects uppercase
					await mutateCoverPictureWidth({
						variables: {
							contentId,
							// Casting to GraphQLCoverPictureWidth as structurally equivalent enums are not equal in TS
							coverPictureWidth: coverPictureWidth.toUpperCase() as GraphQLCoverPictureWidth,
						},
					});
				}
			},
			[contentId, mutateCoverPictureWidth, coverPictureState],
		);

		const onSaveReposition = async (position) => {
			const currentCoverPictureId =
				coverPicture.current?.draft?.value.id ||
				(fg('confluence_cover_pic_optimistic_update_fallback')
					? optimisticMediaCoverPictureId || coverPictureState.state.coverPictureId || undefined
					: undefined);
			const currentCoverPicturePosition =
				coverPicture.current?.draft?.value.position ?? DEFAULT_POSITION_VALUE;
			if (!currentCoverPictureId) {
				return;
			}

			// check that the position isn't the same as the current one
			if (position != currentCoverPicturePosition) {
				const version = String((coverPicture.current?.draft?.version || 0) + 1);
				const propertyValue = JSON.stringify({
					id: currentCoverPictureId,
					position,
				});
				coverPicture.current['draft'] = {
					value: { id: currentCoverPictureId, position },
					version: parseInt(version),
				};
				const updatePromise = updateContentProperty({
					propertyKey: propertyKeyForMutations,
					propertyValue,
					isDelete: false,
					hasExistingProperty: hasCoverPicture,
				});
				if (isSmartSpaceOverview && updatePromise) {
					void updatePromise.then(() => {
						void refetchAfterMutation();
					});
				}
			}
		};

		const openMediaBrowser = () => {
			setIsMediaBrowserOpen(true);
		};

		const closeMediaBrowser = () => {
			setIsMediaBrowserOpen(false);
		};

		const createLiveDocHeaderImageAnalyticsEvent = useCallback(
			(isUnsplashImage: boolean) => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'viewed',
						actionSubject: 'LiveDocHeaderImage',
						actionSubjectId: 'liveDocHeaderImage',
						source: 'liveDocHeaderImage',
						attributes: {
							headerImageType: isUnsplashImage ? 'unsplash' : 'user-uploaded',
							isAIGenerationEnabled: isExperimentEnabled(
								'cc_page_experiences_ai_header_image_generation',
							),
							isAIFCAutoGeneratedImageEnabled: isExperimentEnabled(
								'cc_page_experiences_aifc_auto_generated_header_img',
							),
						},
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		useEffect(() => {
			if (hasCoverPicture && isLivePage && isAllowedExternalCoverPicture != null) {
				createLiveDocHeaderImageAnalyticsEvent(isAllowedExternalCoverPicture);
			}
		}, [
			isAllowedExternalCoverPicture,
			isLivePage,
			hasCoverPicture,
			createLiveDocHeaderImageAnalyticsEvent,
		]);

		const effectiveCoverPictureId =
			coverPicture.current.draft?.value.id ||
			(coverPictureState.state.coverPictureId &&
			fg('confluence_cover_pic_optimistic_update_fallback')
				? coverPictureState.state.coverPictureId
				: undefined);
		const effectiveCoverPicturePosition =
			coverPicture.current.draft?.value.position ?? DEFAULT_POSITION_VALUE;
		const optimisticCoverPictureIdForRender =
			optimisticMediaCoverPictureId || coverPictureState.state.coverPictureId;
		const effectiveIsAllowedExternalCoverPicture =
			effectiveCoverPictureId && fg('confluence_cover_pic_optimistic_update_fallback')
				? isAllowedExternalCoverPictureUrl(effectiveCoverPictureId)
				: isAllowedExternalCoverPicture;
		return (
			<TwoStageHydration
				name="editor-page-cover-picture"
				// cc_editor_two_stage_hydration_removal - When cleaning up this experiment, also remove the related TwoStageHydration component
				enabled={!expValEquals('cc_editor_two_stage_hydration_removal', 'editorCoverPicture', true)}
				ssrPlaceholder
			>
				<Flex>
					{hasCoverPicture && (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
						<style
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{
								__html: `
									@container editor-area (width < 760px) {
										.cover-picture-wrapper-dangerously-set-inner-html {
											height: ${smartSpaceOverviewHeight ?? 147}px
										}
									}
									@container editor-area (width >= 4071px) {
										.cover-picture-wrapper-dangerously-set-inner-html {
											height: ${smartSpaceOverviewHeight ?? 750}px
										}
									}
									`,
							}}
						/>
					)}
					<CoverPictureWrapper
						hasCoverPicture={hasCoverPicture}
						isUploadingPicture={isUploadingPicture}
						data-testid="cover-picture-wrapper"
						width={width}
						dynamicCoverImageHeight={dynamicCoverImageHeight}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className="cover-picture-wrapper-dangerously-set-inner-html"
						isFixedWidthImageOption={isFixedWidthImageOption}
						smartSpaceOverviewHeight={smartSpaceOverviewHeight}
						isSmartSpaceOverview={isSmartSpaceOverview}
						isReadOnly={isReadOnly}
					>
						{hasCoverPicture &&
							(mediaConfig || hasSSRConfigInSSRModeOrHydration) &&
							(effectiveIsAllowedExternalCoverPicture ? (
								<AllowedExternalCoverPicture
									coverPicture={coverPicture}
									dynamicCoverImageHeight={dynamicCoverImageHeight}
									isFixedWidthImageOption={isFixedWidthImageOption}
									smartSpaceOverviewHeight={smartSpaceOverviewHeight}
									coverPictureDataPosition={effectiveCoverPicturePosition}
									coverPictureDataId={effectiveCoverPictureId || coverPictureDataId}
									isRoundedCorners={isSmartSpaceOverview}
								/>
							) : (
								<MediaCoverPicture
									coverPicture={coverPicture}
									mediaConfig={mediaConfig}
									ssrConfig={ssrConfig}
									contentId={contentId}
									coverPictureId={effectiveCoverPictureId}
									coverPicturePosition={effectiveCoverPicturePosition}
									showErrorFlag={showErrorFlag}
									hasSSRConfigInSSRModeOrHydration={hasSSRConfigInSSRModeOrHydration}
									useEditorMediaConfig={
										optimisticCoverPictureIdForRender === effectiveCoverPictureId &&
										fg('confluence_cover_pic_optimistic_update_fallback')
									}
									isFixedWidthImageOption={isFixedWidthImageOption}
									smartSpaceOverviewHeight={smartSpaceOverviewHeight}
									isAIGenerated={!!aiHeaderImageFileId}
									isRoundedCorners={isSmartSpaceOverview}
								/>
							))}
						{!isReadOnly && (
							<ImagePicker
								hasCoverPicture={hasCoverPicture}
								openMediaBrowser={openMediaBrowser}
								onRemoveImage={onRemoveImage}
								userOffline={userOffline}
								isUploadingPicture={isUploadingPicture}
								handleUnsplashUpload={handleUnsplashUpload}
								setIsUploadingPicture={setIsUploadingPicture}
								initialPicturePosition={effectiveCoverPicturePosition}
								onSaveReposition={onSaveReposition}
								shouldShowReposition={shouldShowReposition}
								setShouldShowReposition={setShouldShowReposition}
								coverPictureState={coverPictureState}
								floatingToolbarButtonStyle={floatingToolbarButtonStyle}
								forceUpdate={forceUpdate}
								contentId={contentId}
								dynamicCoverImageHeight={dynamicCoverImageHeight}
								smartSpaceOverviewHeight={smartSpaceOverviewHeight}
								onImageWidthChange={isSmartSpaceOverview ? undefined : onImageWidthChange}
								isRoundedCorners={isSmartSpaceOverview}
								spaceKey={spaceKey}
								isFixedWidthImageOption={isFixedWidthImageOption}
								generateAIHeaderImage={
									generateAIHeaderImage ? wrappedGenerateAIHeaderImage : undefined
								}
								// AI Image Acceptance Flow props
								shouldShowAIImageAcceptanceFlow={
									coverPictureState.state.shouldShowAIImageAcceptanceFlow
								}
								onAcceptAIGeneratedImage={onAcceptAIGeneratedImage}
								onRetryAIGeneratedImage={onRetryAIGeneratedImage}
								isRetryingAIImage={isRetryingAIImage}
								analyticsSource={analyticsSource}
							/>
						)}
					</CoverPictureWrapper>
					{!isReadOnly && mediaConfig && (
						<Browser
							mediaClientConfig={{
								authProvider: mediaConfig?.authProvider,
								useSha256ForUploads: mediaConfig?.useSha256ForUploads,
							}}
							config={{
								multiple: false,
								fileExtensions: ['.jpg', '.jpeg', '.png', '.gif'],
								uploadParams: {
									collection: mediaConfig.collection,
									onUploadRejection: mediaConfig?.onUploadRejection,
								},
							}}
							isOpen={isMediaBrowserOpen}
							onClose={closeMediaBrowser}
							onUploadsStart={handleUploadStart}
							onError={showErrorFlag}
							onEnd={handleUploadEnd}
						/>
					)}
				</Flex>
			</TwoStageHydration>
		);
	},
);

export const EditorPageCoverPicture = withFlags(
	withTransparentErrorBoundary({
		attribution: Attribution.PAGE_EXTENSIONS,
	})(EditorPageCoverPictureComponent),
);
